import { useState } from 'react';
import Alert from '@mui/material/Alert';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AssetSchema,
  CustodyAssignmentSchema,
  type AssetDto,
  type EmployeeDto,
  type ProjectDto,
} from '@taqon/contracts';
import { postJson } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { FormDialog } from '../../shared/ui/forms';
import { ActiveProjectPicker } from '../crm/common';
import { EmployeePicker, MasterSelect } from '../finance/lookups';
import {
  ASSETS,
  businessToday,
  currentCustody,
  Custodian,
  CUSTODY,
  R1_STATUSES,
  statusLabel,
  useMasters,
} from './shared';

/**
 * The custody and status actions on one asset (FRD-AST-024 to AST-027, AST-041), each a real API
 * call behind a dialog that says what will happen. Every one carries the version the page read, so
 * a change based on stale data is refused rather than applied.
 */

async function refresh(queryClient: ReturnType<typeof useQueryClient>): Promise<void> {
  await queryClient.invalidateQueries({ queryKey: ['assets'] });
}

/** The custodian types (FRD-AST-024; ERD §7.4). A project is chosen from the projects it may see. */
const CUSTODIAN_CHOICES = [
  ['employee', 'Employee'],
  ['department', 'Department'],
  ['location', 'Location'],
  ['project', 'Project'],
  ['pool', 'Pool (unassigned stock)'],
  ['third_party', 'Third party'],
] as const;

export function AssignDialog({ asset, onClose }: { asset: AssetDto; onClose: () => void }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const masters = useMasters();
  const canSeeEmployees = hasCapability(session.data, 'hr.employee:view');
  const canSeeProjects = hasCapability(session.data, 'projects.project:view');
  const [type, setType] = useState<string>('employee');
  const [employee, setEmployee] = useState<EmployeeDto | null>(null);
  const [departmentId, setDepartmentId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [project, setProject] = useState<ProjectDto | null>(null);
  const [thirdPartyName, setThirdPartyName] = useState('');
  const [date, setDate] = useState(businessToday(session.data?.businessTimezone));

  const reference: Record<string, string> | null =
    type === 'employee'
      ? employee === null
        ? null
        : { employeeId: employee.id }
      : type === 'department'
        ? departmentId === ''
          ? null
          : { departmentId }
        : type === 'location'
          ? locationId === ''
            ? null
            : { locationId }
          : type === 'project'
            ? project === null
              ? null
              : { projectId: project.id }
            : type === 'third_party'
              ? thirdPartyName.trim() === ''
                ? null
                : { thirdPartyName: thirdPartyName.trim() }
              : {};
  const assign = useMutation({
    mutationFn: () =>
      postJson(
        `${ASSETS}/${asset.id}/assign`,
        { custodianType: type, assignmentDate: date, ...reference },
        CustodyAssignmentSchema,
        session.data?.csrfToken ?? '',
        { expectedVersion: asset.version },
      ),
    onSuccess: async () => {
      await refresh(queryClient);
      onClose();
    },
  });
  const current = currentCustody(asset);
  return (
    <FormDialog
      open
      title={`Assign ${asset.code}`}
      submitLabel="Assign"
      canSubmit={reference !== null && date !== ''}
      submitting={assign.isPending}
      error={assign.isError ? assign.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        assign.mutate();
      }}
    >
      {current !== null && (
        <Alert severity="info" variant="outlined">
          It is now with <Custodian custody={current} />. Assigning it closes that assignment on the
          assignment date; the history keeps both.
        </Alert>
      )}
      <TextField
        select
        label="Custodian type"
        required
        value={type}
        onChange={(e) => {
          setType(e.target.value);
        }}
      >
        {CUSTODIAN_CHOICES.map(([value, label]) => (
          <MenuItem key={value} value={value}>
            {label}
          </MenuItem>
        ))}
      </TextField>
      {type === 'employee' &&
        (canSeeEmployees ? (
          <EmployeePicker
            label="Custodian employee"
            required
            value={employee}
            onChange={setEmployee}
          />
        ) : (
          <Typography variant="caption" color="text.secondary">
            Choosing an employee needs permission to view employees.
          </Typography>
        ))}
      {type === 'department' && (
        <MasterSelect
          label="Department"
          required
          value={departmentId}
          onChange={setDepartmentId}
          options={masters.departments.filter((d) => d.status === 'active')}
          disabled={!masters.can.departments}
        />
      )}
      {type === 'location' && (
        <MasterSelect
          label="Location"
          required
          value={locationId}
          onChange={setLocationId}
          options={masters.locations.filter((l) => l.status === 'active')}
          disabled={!masters.can.locations}
        />
      )}
      {type === 'project' &&
        (canSeeProjects ? (
          <ActiveProjectPicker label="Project" value={project} onChange={setProject} />
        ) : (
          <Typography variant="caption" color="text.secondary">
            Choosing a project needs permission to view projects.
          </Typography>
        ))}
      {type === 'third_party' && (
        <TextField
          label="Third party name"
          required
          value={thirdPartyName}
          onChange={(e) => {
            setThirdPartyName(e.target.value);
          }}
        />
      )}
      <TextField
        label="Assignment date"
        type="date"
        required
        value={date}
        onChange={(e) => {
          setDate(e.target.value);
        }}
        slotProps={{ inputLabel: { shrink: true } }}
      />
    </FormDialog>
  );
}

export function ReturnDialog({ asset, onClose }: { asset: AssetDto; onClose: () => void }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [date, setDate] = useState(businessToday(session.data?.businessTimezone));
  const [condition, setCondition] = useState('');
  const record = useMutation({
    mutationFn: () =>
      postJson(
        `${CUSTODY}/${asset.currentAssignmentId ?? ''}/return`,
        { returnDate: date, condition: condition.trim() },
        CustodyAssignmentSchema,
        session.data?.csrfToken ?? '',
        { expectedVersion: asset.currentAssignmentVersion ?? 0 },
      ),
    onSuccess: async () => {
      await refresh(queryClient);
      onClose();
    },
  });
  return (
    <FormDialog
      open
      title={`Record the return of ${asset.code}`}
      submitLabel="Record return"
      canSubmit={date !== '' && condition.trim() !== ''}
      submitting={record.isPending}
      error={record.isError ? record.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        record.mutate();
      }}
    >
      <Alert severity="warning" variant="outlined">
        This closes the current assignment to <Custodian custody={currentCustody(asset)} />. It
        cannot be reopened.
      </Alert>
      <TextField
        label="Return date"
        type="date"
        required
        value={date}
        helperText={`Not before the assignment date (${asset.custodySince ?? '—'}).`}
        onChange={(e) => {
          setDate(e.target.value);
        }}
        slotProps={{ inputLabel: { shrink: true } }}
      />
      <TextField
        label="Condition at return"
        required
        multiline
        minRows={2}
        value={condition}
        onChange={(e) => {
          setCondition(e.target.value);
        }}
      />
    </FormDialog>
  );
}

/** The custodian confirms they hold the asset (FRD-AST-025). */
export function AcknowledgeDialog({ asset, onClose }: { asset: AssetDto; onClose: () => void }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const acknowledge = useMutation({
    mutationFn: () =>
      postJson(
        `${CUSTODY}/${asset.currentAssignmentId ?? ''}/acknowledge`,
        {},
        CustodyAssignmentSchema,
        session.data?.csrfToken ?? '',
        { expectedVersion: asset.currentAssignmentVersion ?? 0 },
      ),
    onSuccess: async () => {
      await refresh(queryClient);
      onClose();
    },
  });
  return (
    <FormDialog
      open
      title={`Acknowledge ${asset.code}`}
      submitLabel="Acknowledge"
      canSubmit
      submitting={acknowledge.isPending}
      error={acknowledge.isError ? acknowledge.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        acknowledge.mutate();
      }}
    >
      <Typography>
        You confirm that {asset.code} — {asset.name} was assigned to you on{' '}
        {asset.custodySince ?? '—'}. The acknowledgement is recorded with your name and the time.
      </Typography>
    </FormDialog>
  );
}

/** A manual lifecycle status change with its mandatory reason (FRD-AST-041, AST-050). */
export function StatusDialog({ asset, onClose }: { asset: AssetDto; onClose: () => void }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const targets = R1_STATUSES.filter((s) => s !== asset.lifecycleStatus);
  const [toStatus, setToStatus] = useState<string>(targets[0] ?? '');
  const [date, setDate] = useState(businessToday(session.data?.businessTimezone));
  const [reason, setReason] = useState('');
  const change = useMutation({
    mutationFn: () =>
      postJson(
        `${ASSETS}/${asset.id}/status`,
        { toStatus, effectiveDate: date, reason: reason.trim() },
        AssetSchema,
        session.data?.csrfToken ?? '',
        { expectedVersion: asset.version },
      ),
    onSuccess: async () => {
      await refresh(queryClient);
      onClose();
    },
  });
  return (
    <FormDialog
      open
      title={`Change the status of ${asset.code}`}
      submitLabel="Change status"
      canSubmit={toStatus !== '' && date !== '' && reason.trim() !== ''}
      submitting={change.isPending}
      error={change.isError ? change.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        change.mutate();
      }}
    >
      <Typography variant="body2" color="text.secondary">
        Now: {statusLabel(asset.lifecycleStatus)}. Lost, stolen, on loan and disposal are not
        available in this release.
      </Typography>
      <TextField
        select
        label="New status"
        required
        value={toStatus}
        onChange={(e) => {
          setToStatus(e.target.value);
        }}
      >
        {targets.map((s) => (
          <MenuItem key={s} value={s}>
            {statusLabel(s)}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label="Effective date"
        type="date"
        required
        value={date}
        onChange={(e) => {
          setDate(e.target.value);
        }}
        slotProps={{ inputLabel: { shrink: true } }}
      />
      <TextField
        label="Reason"
        required
        multiline
        minRows={2}
        value={reason}
        onChange={(e) => {
          setReason(e.target.value);
        }}
      />
    </FormDialog>
  );
}
